/**
 * Shared ladder primitives — pricing helpers, level sanity checks, and the
 * fee-adjusted-odds formula. Imported by both GameTable (moneyline ladder)
 * and SpreadsTotals (spread/total per-line ladder). Row rendering itself is
 * NOT shared — the two views have meaningfully different columns; only the
 * pricing math is common.
 */

import type { BookLevel, MarketSide, PriceMode, VenueBook } from '../types';
import { bestPriceCentsForOrderSide } from '../tradeModalBook';
import { estimateOrderFeeCents } from '@/lib/pricing/fees';
import {
  fromCentiCents,
  mirrorCents,
  roundToCentiCents,
  tickCentsFor,
  toCentiCents,
} from '@/lib/pricing/subCent';
import type { useFeeFor } from '@/services/hydraFees';

export const LADDER_LEVELS = 3;

/**
 * Slack around the last trade for sanity-filtering orderbook levels. Kalshi
 * occasionally sends bid levels well above the prevailing ask (and vice
 * versa) — usually tiny stub orders or stale ones that its own UI hides.
 */
export const LEVEL_SANITY_SLACK = 15;

/** Levels with sub-contract size are always phantoms. */
export function hasSize(l: BookLevel): boolean {
  return l.size >= 1;
}

/**
 * Filter for legitimate bid levels on whichever side `lastTradeCents`
 * represents. A bid is "real" if it's at or below the last trade plus a
 * modest slack; bids materially above the last trade cross the book and
 * are almost always stale or stub orders that Kalshi itself hides.
 */
export function bidIsReal(lastTradeCents: number | null | undefined) {
  return (l: BookLevel): boolean => {
    if (!hasSize(l)) return false;
    if (lastTradeCents == null) return true;
    return l.priceCents <= lastTradeCents + LEVEL_SANITY_SLACK;
  };
}

/**
 * The classic 3-level ladder view of one side of a binary book: own-side
 * bids (join/rest targets) plus the opposite side's bids MIRRORED into asks
 * (cross targets), both sanity-filtered by `bidIsReal` and capped at
 * LADDER_LEVELS. Single implementation shared by the spread/total inline
 * ladder and the LT2 TwoSidedLadder so the two can never disagree on what
 * a level's price means. Mirrors in the centi-cent integer domain
 * (`mirrorCents`, not `100 − x`) — the #535 decicent discipline.
 */
export function buildSideLevels(
  book: VenueBook | null | undefined,
  side: MarketSide,
): { asks: BookLevel[]; bids: BookLevel[] } {
  if (!book) return { asks: [], bids: [] };
  const ownBids = side === 'yes' ? book.yes : book.no;
  const oppositeBids = side === 'yes' ? book.no : book.yes;
  const lastYes = book.lastPriceCents;
  const ownLast = lastYes == null ? null : side === 'yes' ? lastYes : mirrorCents(lastYes);
  const oppositeLast = lastYes == null ? null : side === 'yes' ? mirrorCents(lastYes) : lastYes;
  return {
    asks: oppositeBids
      .filter(bidIsReal(oppositeLast))
      .slice(0, LADDER_LEVELS)
      .map((l) => ({ priceCents: mirrorCents(l.priceCents), size: l.size })),
    bids: ownBids.filter(bidIsReal(ownLast)).slice(0, LADDER_LEVELS),
  };
}

/**
 * Best ask price for a given market side, derived from the opposite side's
 * bids. YES ask = 100 − best NO bid; NO ask = 100 − best YES bid.
 */
export function bestAskCentsForBook(
  venueBook: VenueBook | null | undefined,
  marketSide: MarketSide,
): number | null {
  if (!venueBook) return null;
  const opposite = marketSide === 'yes' ? venueBook.no : venueBook.yes;
  const lastYes = venueBook.lastPriceCents;
  const oppositeLast =
    lastYes == null ? null : marketSide === 'yes' ? mirrorCents(lastYes) : lastYes;
  const bestOppositeBid = opposite.find(bidIsReal(oppositeLast))?.priceCents;
  return bestOppositeBid != null ? mirrorCents(bestOppositeBid) : null;
}

/**
 * Best-bid depth (contracts resting at the highest standing bid) on a given
 * market side. Returns null when the side has no real bid level — same
 * `bidIsReal` sanity filter pillPriceCents uses, so stub/stale levels above
 * the last trade don't masquerade as the front of book. Used by the
 * dashboard's K Q column + sort-by-queue, and reusable anywhere a "how many
 * contracts sit ahead at the front" read is needed without a user order.
 */
export function bestBidDepthForSide(
  book: VenueBook | null | undefined,
  side: MarketSide,
): number | null {
  if (!book) return null;
  const levels = side === 'yes' ? book.yes : book.no;
  const lastYes = book.lastPriceCents;
  const sideLast = lastYes == null ? null : side === 'yes' ? lastYes : mirrorCents(lastYes);
  const best = levels.find(bidIsReal(sideLast));
  return best?.size ?? null;
}

/**
 * Pill price for a (book, side) under the toolbar's PriceMode.
 *   - maker  → highest standing bid on this side's ladder
 *   - taker  → lowest ask on this side (= 100 − best opposite bid)
 *   - market → last trade price (lastPriceCents for YES, 100 − last for NO)
 * Returns `null` when the chosen mode has no data — the pill renders "—".
 *
 * BOOKLESS fallback (poly-props-events, 2026-07-17): a book with ZERO levels
 * on both sides falls back to the frame BBO scalars (yesBidCents/yesAskCents,
 * sentinel-guarded at build). Static Poly props carry no orderbook (never
 * WS-subscribed — hydra 404s them) and often no last trade, so without this
 * every mode rendered "—" despite a live joined price. The NO side derives
 * as the YES complement (the contracted 100−yes fallback). Levels ALWAYS win
 * when any exist — preferring the frame scalar over the book is the f899b43
 * frozen-pill class; this fires only where no book will ever arrive.
 */
export function pillPriceCents(
  book: VenueBook | null | undefined,
  side: MarketSide,
  mode: PriceMode,
): number | null {
  if (!book) return null;
  const bookless = book.yes.length === 0 && book.no.length === 0;
  if (mode === 'maker') {
    const fromBook = bestPriceCentsForOrderSide(book, side) ?? null;
    if (fromBook != null || !bookless) return fromBook;
    // Bid on YES = yesBid; bid on NO = 100 − yesAsk.
    return side === 'yes'
      ? book.yesBidCents ?? null
      : book.yesAskCents != null ? mirrorCents(book.yesAskCents) : null;
  }
  if (mode === 'market') {
    const last = book.lastPriceCents;
    if (last != null) return side === 'yes' ? last : mirrorCents(last);
    if (!bookless || book.priceCents == null) return null;
    // Never-traded bookless market: the sentinel-guarded mid is the only
    // honest "market" read available.
    return side === 'yes' ? book.priceCents : mirrorCents(book.priceCents);
  }
  const fromBook = bestAskCentsForBook(book, side);
  if (fromBook != null || !bookless) return fromBook;
  // Ask on YES = yesAsk; ask on NO = 100 − yesBid.
  return side === 'yes'
    ? book.yesAskCents ?? null
    : book.yesBidCents != null ? mirrorCents(book.yesBidCents) : null;
}

// ── Crossed-book tripwire (#167) ──────────────────────────────────────
// A book is CROSSED when its best bid sits ABOVE its best ask — impossible
// on a healthy market. HydraEdge now detects and self-heals persistent Kalshi
// crossed books (`eb9ecf5` 30s rebaseline backstop, `b37a84e` ~2s priority
// REST adjudication), but during that repair window G1 rendered the broken
// book as an ordinary executable ladder. This is the DISPLAY-ONLY tripwire
// from the #139 close-out: it adds provenance, never a local repair.
//
// STRICTLY display-only. Nothing here rewrites, complements, clamps or
// otherwise touches a price, size, order request, feed-ownership decision or
// accounting value — callers keep the exact cents they already had, and the
// pill's click still dispatches the unmodified displayed price.

/**
 * Kalshi's book tick in cents for tripwire purposes.
 *
 * Deliberately NOT `tickCentsFor('kalshi', …)` (0.1¢). That helper's 0.1 is
 * the loosest-published NEW-ORDER placement guess — safe there because an
 * off-grid order is simply rejected venue-side. As a crossed-book allowance
 * it would be wrong twice over: it would flag a whole-cent market's valid
 * one-tick boundary, and it contradicts the ≥2¢ threshold the feed side
 * actually measured at (#139: "43/392 Edge books at least 2¢ off OG").
 *
 * Why 1¢, and what happens when that stops being true:
 *   - Per #63, as of 2026-07-24 no g1-traded sports series is on a finer
 *     grid; Kalshi's early finer-tick targets are macro/crypto. That is a
 *     DATED claim — #63 puts Kalshi's pilot markets in the week of
 *     2026-07-27 and expansion on 08-03, so it will age.
 *   - It ages SAFELY. On a finer-grid market a 1¢ allowance is wider than
 *     one tick, so the tripwire stays quiet on small crosses it could in
 *     principle flag. It under-reports; it never false-alarms on a valid
 *     one-tick boundary. For a tripwire that is the correct direction to
 *     fail, and it still catches the whole #139 defect class (≥2¢).
 *   - There is nothing finer to read anyway: #63 confirms the authoritative
 *     per-market `priceRanges` grid is NOT in the live feed (the Hydra and
 *     HydraEdge producer commits were still local-only at 2026-07-24), and a
 *     live prod frame carries no such field. When that producer half lands,
 *     this is the one place that should start reading `priceRanges[].step`.
 */
export const KALSHI_BOOK_TICK_CENTS = 1;

/**
 * Ticks of crossing tolerated before a book is called suspect. The feed's
 * contract is an ALLOWANCE, not zero tolerance ("crossed beyond the one-tick
 * allowance", #139), so ordinary equality (a locked book) and a single valid
 * tick of overlap both stay quiet; only a cross STRICTLY beyond one tick trips.
 */
export const CROSSED_BOOK_TICK_ALLOWANCE = 1;

/** The tick a crossed read is judged against. Polymarket's CLOB tick is
 *  authoritative and arrives on the frame, so it is used as-is; Kalshi has no
 *  per-market tick in the live feed (see {@link KALSHI_BOOK_TICK_CENTS}). */
export function crossedBookTickCents(
  book: VenueBook | null | undefined,
  venue: 'kalshi' | 'polymarket',
): number {
  return venue === 'polymarket'
    ? tickCentsFor('polymarket', book?.tickSize)
    : KALSHI_BOOK_TICK_CENTS;
}

/** A displayed book whose best bid crosses its best ask beyond the allowance. */
export interface CrossedBookRead {
  /** Best bid as DISPLAYED, in YES-side cents. */
  bidCents: number;
  /** Best ask as DISPLAYED, in YES-side cents. */
  askCents: number;
  /** How far the bid overshoots the ask, in cents. Always > tickCents. */
  crossCents: number;
  /** The tick this read was judged against. */
  tickCents: number;
}

/**
 * Tripwire read for a book, or null when the book is sane / unjudgeable.
 *
 * Detection runs on the SAME `pillPriceCents` reads that produce the numbers
 * on screen, so the tripwire can never disagree with what the trader is
 * looking at. That covers the bookless frame-BBO fallback, and it means a
 * phantom level the `bidIsReal` sanity filter already hides is NOT counted —
 * a level that is not displayed cannot make a DISPLAYED book crossed.
 *
 * Read in maker/taker space regardless of the toolbar's PriceMode: the book's
 * sanity is a property of the book, not of which number the toolbar happens
 * to show. Same reasoning as GameTable's `kalshiValueSignal`, which is always
 * the taker read for the same reason.
 *
 * The condition is symmetric across sides — on a Kalshi-shaped book the YES
 * ask is `100 − best NO bid`, so `yesBid − yesAsk` equals `yesBid + noBid −
 * 100`, and the NO side yields the identical figure. One read therefore
 * qualifies BOTH pills of a book.
 *
 * Nothing is cached: the read is recomputed from the current book, so it
 * clears by itself the moment the book heals or feed ownership falls back to
 * a sane source.
 */
export function crossedBookRead(
  book: VenueBook | null | undefined,
  venue: 'kalshi' | 'polymarket',
): CrossedBookRead | null {
  if (!book) return null;
  const bidCents = pillPriceCents(book, 'yes', 'maker');
  const askCents = pillPriceCents(book, 'yes', 'taker');
  // A one-sided book makes no cross claim — absent data is not evidence of a
  // defect, and inventing one would be the opposite of this ticket's point.
  if (bidCents == null || askCents == null) return null;
  // Integer centi-cents: `48.3 - 44.1` is 4.199999999999996 in float64, which
  // both mis-renders and mis-compares against the tick (CENTI_CENT_DOMAIN).
  const crossCc = toCentiCents(bidCents) - toCentiCents(askCents);
  const tickCents = crossedBookTickCents(book, venue);
  const allowanceCc = toCentiCents(tickCents) * CROSSED_BOOK_TICK_ALLOWANCE;
  if (crossCc <= allowanceCc) return null;
  return { bidCents, askCents, crossCents: fromCentiCents(crossCc), tickCents };
}

/**
 * The tripwire's wording, shared so the surfaces can't word it differently
 * (the eight-copies-drifted lesson from #154's cue strings).
 *
 * Written for the TRADER, and kept to the two sentences one can read on a
 * transient hover — the issue's only stated wording constraint is "concise".
 * "which shouldn't happen" carries the meaning for anyone who doesn't know
 * the term "crossed"; the ¢ figures let them verify it against the pills
 * themselves.
 *
 * It deliberately does NOT promise the fill will differ — #139 explicitly
 * could not establish that for the reported trade.
 *
 * An earlier draft appended: "Shown unmodified — G1 has not corrected the
 * book." Dropped on purpose. The "adds provenance rather than pretending to
 * repair data locally" criterion constrains BEHAVIOUR, not copy, and that
 * behaviour is pinned by tests (a clamped price fails
 * PricePill.crossedBook.test.tsx) rather than announced in the UI. "G1" is
 * also not trader vocabulary — the #139 report says "the Dash". Restore that
 * sentence here if the house voice wants the assurance stated explicitly.
 */
export function crossedBookTooltip(read: CrossedBookRead): string {
  return (
    `CROSSED BOOK — bid ${read.bidCents}¢ is above ask ${read.askCents}¢ by ` +
    `${read.crossCents}¢, which shouldn’t happen. The price feed is repairing it; ` +
    `this price may not be executable right now.`
  );
}

/**
 * Fee perspective for {@link effectivePriceForOdds} — controls whether
 * the maker or taker per-series rate is applied.
 *
 *   'maker' → bid / resting view (LIMIT order placement, dashboard's
 *             Maker price-mode pill, bid-side ladder row). Uses
 *             feeEntry.feeMakerRate.
 *   'taker' → ask / crossing view (MARKET order, Taker price-mode pill,
 *             ask-side ladder row). Uses feeEntry.feeTakerRate.
 *
 * Pre-correctness leg (2026-05-25): this function always used the maker
 * rate regardless of the surface, so a Taker-mode pill on a
 * quadratic_with_maker_fees series under-quoted the fee by ~4x, and on
 * a plain quadratic series (maker rate = 0) the Fees toggle silently
 * did nothing. Callers now thread their perspective in explicitly.
 */
export type FeePerspective = 'maker' | 'taker';

/**
 * Fee-adjusted effective price in cents matching the order entry's formula:
 * effective = raw cents + per-contract fee. The per-contract fee is the raw
 * hydra formula value (centicent precision) — NOT floored to 1¢ and NOT
 * scaled by any calibration coefficient. (Kalshi's 1¢ minimum is a per-fill
 * billing floor, not a per-contract one; applying it here over-stated a
 * single contract's odds.) Returns raw cents when the Fees toggle is off,
 * the ticker has no fee schedule, the schedule is `flat`, or the selected
 * perspective's fee rate is 0 (Polymarket either way, Kalshi `quadratic`
 * series in maker perspective).
 *
 * IMPORTANT: display-only — the return value is consumed only by
 * americanOdds() and the per-row odds tooltip. Never feeds order
 * placement cents (callers keep the raw priceCents they had pre-call).
 */
export function effectivePriceForOdds(
  cents: number,
  feeAdjusted: boolean,
  feeEntry: ReturnType<typeof useFeeFor>,
  perspective: FeePerspective,
  makerRebatePct = 0,
): number {
  if (!feeAdjusted || !feeEntry || feeEntry.feeType === 'flat') return cents;
  const feeRate =
    perspective === 'taker'
      ? (feeEntry.feeTakerRate ?? 0)
      : (feeEntry.feeMakerRate ?? 0);
  const perContractCents = estimateOrderFeeCents({ priceCents: cents, count: 1, feeRate });
  // Maker-fee rebate (Kalshi Market Maker Program): a fraction of the maker
  // fee, 0..1. Only applies to the maker perspective — there's no taker
  // rebate. `makerRebatePct` is 0 unless the org is enrolled and the trader
  // has the MM Rebate toggle on. See lib/pricing/makerRebate.
  const netFeeCents =
    perspective === 'maker' && makerRebatePct > 0
      ? perContractCents * (1 - Math.min(makerRebatePct, 1))
      : perContractCents;
  if (netFeeCents <= 0) return cents;
  const effective = cents + netFeeCents;
  return effective > 0 ? effective : 0.01;
}

/** The two prices behind one quote: what the venue executes, and what the
 *  American odds beside it were derived from. See {@link pricePresentation}. */
export interface PricePresentation {
  /** The literal executable level, quantized to the 0.01¢ grain. This is what
   *  a click dispatches and what an order payload carries. */
  rawCents: number;
  /** The odds' input. Equals `rawCents` in Raw mode, on a venue with no fee
   *  schedule, and wherever the applicable rate is 0. */
  effectiveCents: number;
  perspective: FeePerspective;
}

/**
 * One display contract for every executable price the trading UI quotes.
 *
 * Surfaces disagree about which of the two prices to LABEL — dense ladder rows
 * keep the raw venue level (#521), roomier surfaces show the effective one so
 * the cents are literally the odds' input (#452) — but they must never
 * disagree about the odds. Deriving that basis here, once, is what keeps the
 * Value Dashboard ladder, the spreads/totals ladder and the order ticket from
 * quoting three American lines for one price.
 *
 * Display-only: neither returned number feeds order placement. Callers keep
 * the raw `cents` they passed in for any wire payload.
 */
export function pricePresentation(
  cents: number,
  perspective: FeePerspective,
  feeAdjusted: boolean,
  feeEntry: ReturnType<typeof useFeeFor>,
  makerRebatePct = 0,
): PricePresentation {
  return {
    rawCents: roundToCentiCents(cents),
    effectiveCents: roundToCentiCents(
      effectivePriceForOdds(cents, feeAdjusted, feeEntry, perspective, makerRebatePct),
    ),
    perspective,
  };
}

/**
 * {@link pricePresentation} for a dense order-book row, which reads its fee
 * perspective off the row itself: an ask is crossed as a taker, a bid rests as
 * a maker. That mapping is the ladder's own knowledge — a surface with no
 * bid/ask rows (the order ticket) resolves its perspective differently and
 * calls the general form.
 */
export function ladderRowPricePresentation(
  cents: number,
  kind: 'ask' | 'bid',
  feeAdjusted: boolean,
  feeEntry: ReturnType<typeof useFeeFor>,
  makerRebatePct = 0,
): PricePresentation {
  return pricePresentation(
    cents,
    kind === 'ask' ? 'taker' : 'maker',
    feeAdjusted,
    feeEntry,
    makerRebatePct,
  );
}

/** Format a contract volume into "1.2M / 12.3K / 999". */
export function formatVolume(vol: number): string {
  if (vol === 0) return '—';
  if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(1)}M`;
  if (vol >= 10_000) return `${(vol / 1_000).toFixed(1)}K`;
  const rounded = Math.round(vol);
  if (rounded >= 1_000) return rounded.toLocaleString();
  return String(rounded);
}

/** Cumulative depth ahead of a user's resting order — `atLevel` is the
 *  order's own queue position; `atLevelOrBetter` adds same-side bid depth
 *  at strictly more aggressive prices. */
export function queueDepth(
  order: {
    action: 'buy' | 'sell';
    side: 'yes' | 'no';
    priceCents: number;
    queuePosition: number | null;
  },
  book: VenueBook | null,
): { atLevel: number | null; atLevelOrBetter: number | null } {
  if (order.queuePosition == null) return { atLevel: null, atLevelOrBetter: null };
  if (!book) return { atLevel: order.queuePosition, atLevelOrBetter: null };
  // "Better" = priced more aggressively, fills first.
  //   • BUY  → other bids on same side at strictly higher prices.
  //   • SELL → other asks on same side at strictly lower prices, which on
  //            Kalshi's book live as opposite-side bids at strictly higher
  //            prices (an ask at P = opposite bid at 100−P).
  const isSell = order.action === 'sell';
  const refLevels = isSell
    ? (order.side === 'yes' ? book.no : book.yes)
    : (order.side === 'yes' ? book.yes : book.no);
  const threshold = isSell ? mirrorCents(order.priceCents) : order.priceCents;
  let better = 0;
  for (const l of refLevels) {
    if (l.priceCents > threshold) better += l.size;
  }
  return {
    atLevel: order.queuePosition,
    atLevelOrBetter: order.queuePosition + better,
  };
}

/** Hover-tooltip text matching the inline O-ring tooltip on the moneyline
 *  PosCell, used by per-row user-order chips so the two stay verbatim. */
export function orderTooltip(
  order: {
    action: 'buy' | 'sell';
    side: 'yes' | 'no';
    priceCents: number;
    remainingCount: number;
    queuePosition: number | null;
    /** #666 display-lane provenance — present on displayOrdersByTicker rows. */
    queueRetained?: boolean;
    queueConfirmedAtMs?: number | null;
  },
  book: VenueBook | null,
): string {
  const depth = queueDepth(order, book);
  const totalRounded = depth.atLevelOrBetter != null ? Math.round(depth.atLevelOrBetter) : null;
  const levelRounded = depth.atLevel != null ? Math.round(depth.atLevel) : null;
  // #666: a retained (last-confirmed) queue value carries its age so the
  // number never masquerades as a live reading. Same vocabulary as the
  // QueueVintageBadge's "last confirmed" framing.
  const retainedNote =
    order.queueRetained && order.queueConfirmedAtMs != null
      ? ` (last confirmed ${Math.max(0, Math.round((Date.now() - order.queueConfirmedAtMs) / 1000))}s ago)`
      : order.queueRetained
        ? ' (last confirmed value)'
        : '';
  const detail =
    totalRounded != null
      ? `${totalRounded.toLocaleString()} ahead total${
          levelRounded != null ? `, ${levelRounded.toLocaleString()} at this level` : ''
        }${retainedNote}`
      : levelRounded != null
        ? `${levelRounded.toLocaleString()} ahead at this level${retainedNote}`
        : 'queue data temporarily unavailable; order remains current';
  return `${order.action.toUpperCase()} ${order.side.toUpperCase()} ${order.remainingCount} @ ${order.priceCents}¢ — ${detail}`;
}

/** Per-price-level user-order chip data — qty + queue depth + tooltip. */
export interface BidRowOrder {
  orderId: string;
  remainingCount: number;
  /** Total contracts queued ahead at this price + better prices. */
  queueOrBetter: number | null;
  tooltip: string;
  /**
   * #666 display-lane provenance: this queue reading is a RETAINED
   * last-confirmed value, not a live one. Absent (or false) alongside a
   * non-null `queueOrBetter` means the venue confirmed it on this snapshot.
   * Carried onto the chip row for #676 — the ranking has to tell a confirmed
   * position from a remembered one, and the number alone cannot.
   */
  queueRetained?: boolean;
}

// ── #676 — which own orders earn the bounded inline slots ──────────────
//
// Every ladder surface bounds the trader's own-order chips at one price level
// and moves the rest behind a `+N` (see `RestingOrderGroup`, and the compact
// band's `COMPACT_BAND_QTY_LIMIT`). Traders on GBuff reported the bound
// keeping the WRONG ones: the buckets were sorted largest-remaining-first, so
// an order with 4,000 contracts ahead of it held an inline slot while the one
// about to fill sat inside the disclosure. Size is not actionability.
//
// The ordering lives next to the truncation rather than in the four builders
// that fill these buckets: whoever cuts the list decides what survives the
// cut, and a caller cannot forget a rule it never has to apply. (#650 shipped
// `TwoSidedLadder` without `priceCents` for exactly that reason.)

/** Rank tier — a strictly worse tier can never outrank a better one, however
 *  small its number. See `rankRestingOrders`.
 *
 *  `Number.isFinite` rather than a null check: a NaN would make the comparator
 *  return NaN, which `Array.prototype.sort` treats as 0 — two orders would
 *  compare EQUAL without ever reaching the tiebreaks, and the arrangement
 *  would silently depend on input order again. Queue values are finite-guarded
 *  where they are parsed (`useUserKalshiPOS`), but that guard lives in another
 *  module and this function must not depend on it holding. */
function queueTier(o: BidRowOrder): number {
  if (!Number.isFinite(o.queueOrBetter)) return 2; // honest unknown
  return o.queueRetained ? 1 : 0; // remembered vs confirmed
}

/**
 * The sentence both truncating surfaces use to name the ordering.
 *
 * Shared so the two cannot drift into describing one rule two ways — the
 * failure this pair was reviewed for. `RestingOrderGroup` puts it on the `+N`
 * tooltip; the compact band leads its own tooltip with it.
 */
export const QUEUE_ORDER_NOTE = 'Nearest the front of the queue first';

/** The INLINE ladder-row arrangement of the same ranked list (V3 exhibit
 *  request): the order nearest the front of the queue holds the RIGHT edge —
 *  visually "next to be eaten" — and later/lower-priority orders extend
 *  left, with the overflow disclosure at the far left. List presentations
 *  (the +N panel, the expanded box) keep reading nearest-first top-down and
 *  keep using QUEUE_ORDER_NOTE. */
export const QUEUE_ROW_ORDER_NOTE = 'Nearest the front of the queue at the right edge';

/** How many of these orders show a remembered or unknown queue position —
 *  the ones the tiers demote below every confirmed reading. */
export function unconfirmedQueueCount(orders: readonly BidRowOrder[]): number {
  return orders.filter((o) => queueTier(o) > 0).length;
}

/**
 * The caveat that goes with {@link QUEUE_ORDER_NOTE}, or '' when there is
 * nothing to caveat.
 *
 * Deliberately "last-confirmed or unknown", NOT "unconfirmed": #666 built the
 * whole retention lane around the phrase "last confirmed", and every retained
 * order's own tooltip already says "last confirmed Ns ago". Two names for one
 * state on one screen is how a trader concludes they are looking at two
 * different states.
 *
 * `subject` names WHICH orders are being counted — the disclosure counts the
 * ones it is holding back, the compact band counts everything it lists.
 */
export function unconfirmedQueueNote(count: number, subject: string): string {
  if (count <= 0) return '';
  return count === 1
    ? `; one ${subject} order shows a last-confirmed or unknown position`
    : `; ${count} ${subject} orders show last-confirmed or unknown positions`;
}

/**
 * The trader's resting orders at ONE price level, most actionable first.
 *
 * Ordering, in full:
 *   1. confirmed queue readings, fewest contracts ahead first;
 *   2. then RETAINED (last-confirmed) readings, likewise fewest first;
 *   3. then orders whose queue position is unknown.
 *   Ties inside any tier: larger resting size first, then `orderId`.
 *
 * The tiers are strict on purpose. A 300-ahead value remembered from 20s ago
 * is not evidence that the order is nearer the front than one confirmed at
 * 1,400 ahead this second — promoting it would let a stale number quietly
 * outrank a live one, which is the failure the #666 retention lane exists to
 * avoid. Their presentation is untouched: a retained chip still carries its
 * "last confirmed Ns ago" tooltip and an unknown one still reads `?`.
 *
 * `orderId` is the final key so the result depends only on the orders
 * themselves, never on the array order they arrived in — an identical
 * snapshot always produces an identical row, and no permutation of it
 * produces a different one.
 *
 * Every order in one group shares price, side and action, so the "better
 * prices" half of `queueOrBetter` is the same constant for all of them —
 * ranking on it IS ranking on each order's own queue position.
 *
 * Returns a new array; the input is never mutated (these buckets are shared
 * with `CancelLevelControl` and the compact band's tooltip).
 */
export function rankRestingOrders(orders: readonly BidRowOrder[]): BidRowOrder[] {
  return [...orders].sort((a, b) => {
    const tier = queueTier(a) - queueTier(b);
    if (tier !== 0) return tier;
    if (a.queueOrBetter != null && b.queueOrBetter != null && a.queueOrBetter !== b.queueOrBetter) {
      return a.queueOrBetter - b.queueOrBetter;
    }
    if (a.remainingCount !== b.remainingCount) return b.remainingCount - a.remainingCount;
    return a.orderId < b.orderId ? -1 : a.orderId > b.orderId ? 1 : 0;
  });
}

// ── Unabated consensus dot (ladder fair-value marker) ────────────────
// A small dot rendered in each venue's ladder price column at the vertical
// position where the Unabated fair value truly sits, tenth-accurate. The
// ladder's rows are the book's LEVELS, not a contiguous cent scale — levels
// skip prices and K/P rows are index-paired, not price-aligned — so the dot
// is placed per venue by finding the two visible levels that straddle fair
// and interpolating proportionally between their row centers. Position gives
// the coarse read; the exact tenths live in the dot's tooltip.

/** Edge band (±¢) around fair vs the venue's best ask that renders the dot
 *  yellow ("coin toss"). At/beyond +1¢ the ask is cheap vs consensus
 *  (favorable, green); at/beyond −1¢ it's rich (unfavorable, red). */
export const CONSENSUS_DOT_EDGE_BAND_CENTS = 1;

export type ConsensusDotTone = 'favorable' | 'coin-toss' | 'unfavorable' | 'no-ask';

/** Green/yellow/red by the actionable edge: fair − best ask on the active
 *  side (both in active-side cents). 'no-ask' (grey) when the venue has no
 *  visible ask to price against. */
export function consensusDotTone(
  fairActiveCents: number,
  bestAskActiveCents: number | null,
  bandCents = CONSENSUS_DOT_EDGE_BAND_CENTS,
): ConsensusDotTone {
  if (bestAskActiveCents == null) return 'no-ask';
  const edge = fairActiveCents - bestAskActiveCents;
  if (edge >= bandCents) return 'favorable';
  if (edge <= -bandCents) return 'unfavorable';
  return 'coin-toss';
}

export interface ConsensusDotPlacement {
  /** Which ladder block + row index within it the dot anchors to. */
  kind: 'ask' | 'bid';
  idx: number;
  /** Vertical offset from the anchor row's CENTER, in row-heights: 0 = the
   *  anchor's own center, 1 = the next visual row's center. Fractional
   *  values interpolate the gap; can exceed 1 when padding (null) rows sit
   *  between the straddling levels. */
  offsetRows: number;
  /** Set when fair sits beyond the visible book — render as a ▲/▼ pinned to
   *  the top/bottom visible level instead of a positionally-true dot. */
  clamped: 'above' | 'below' | null;
}

/**
 * Locate the fair value on a venue's visual ladder stack. Inputs are the
 * PADDED visual arrays exactly as rendered (asks worst→best downward, then
 * bids best→worst downward, nulls = empty filler rows), so `kind`+`idx`
 * address the same rows the renderer draws. `fairActiveCents` must already
 * be in active-marketSide space (NO side = 100 − yes fair).
 *
 * Prices decrease monotonically down the merged stack on a sane book; on a
 * crossed/dirty book with no bracketing pair the dot falls back to the
 * nearest-priced level (offset 0). Returns null when the venue has no
 * visible levels at all.
 */
export function consensusDotPlacement(
  asksPadded: ReadonlyArray<BookLevel | null>,
  bidsPadded: ReadonlyArray<BookLevel | null>,
  fairActiveCents: number,
): ConsensusDotPlacement | null {
  const askRows = asksPadded.length;
  const rows: Array<{ g: number; price: number }> = [];
  asksPadded.forEach((l, i) => {
    if (l) rows.push({ g: i, price: l.priceCents });
  });
  bidsPadded.forEach((l, i) => {
    if (l) rows.push({ g: askRows + i, price: l.priceCents });
  });
  if (rows.length === 0) return null;
  const place = (
    g: number,
    offsetRows: number,
    clamped: ConsensusDotPlacement['clamped'],
  ): ConsensusDotPlacement =>
    g < askRows
      ? { kind: 'ask', idx: g, offsetRows, clamped }
      : { kind: 'bid', idx: g - askRows, offsetRows, clamped };

  if (fairActiveCents > rows[0].price) return place(rows[0].g, 0, 'above');
  const bottom = rows[rows.length - 1];
  if (fairActiveCents < bottom.price) return place(bottom.g, 0, 'below');
  for (let i = 0; i < rows.length - 1; i++) {
    const a = rows[i];
    const b = rows[i + 1];
    if (a.price >= fairActiveCents && fairActiveCents >= b.price) {
      const span = a.price - b.price;
      // Equal-priced adjacent rows (shouldn't happen on a sane book): sit
      // on the boundary between them.
      const f = span <= 0 ? 0.5 : (a.price - fairActiveCents) / span;
      // NOTE: the 1px asks↔bids separator row is not counted — spanning it
      // adds a sub-pixel positional error, ignored.
      return place(a.g, f * (b.g - a.g), null);
    }
  }
  // Non-monotonic (crossed) book: nearest level, no interpolation.
  let nearest = rows[0];
  for (const r of rows) {
    if (Math.abs(r.price - fairActiveCents) < Math.abs(nearest.price - fairActiveCents)) nearest = r;
  }
  return place(nearest.g, 0, null);
}
